/**
 * Collection liveness — the panel that makes the board honest.
 *
 * A backup board that cannot tell "nothing failed" from "nothing was heard" is
 * worse than no board, because it reassures. `PAYLOAD.md` states the rule
 * plainly: if no report has arrived for more than 2× the expected cadence the
 * status tiles go to *unknown*, never to green. This module renders the *why*
 * behind that switch — which source went quiet, when it last spoke, and which
 * host was supposed to be speaking.
 *
 * Three distinct states are kept apart (design rules §5): **unconfigured** (the
 * ingest endpoint or the Azure tier is not wired up) gets the setup panel;
 * **stale** (wired up, but silent) gets a warning banner; **partial** (data
 * arrived with problems) gets the errors listed rather than swallowed.
 */

import { Alert, Freshness, Typography } from "@norican/react";

import type { BackupCollectorState, BackupOverview } from "@/lib/api";
import { formatRelativeTime } from "@/lib/charts/config";

import { PanelCard } from "./panel-card";
import { sourceLabel } from "./status";

/** The board is not wired up: say exactly what is missing, show no numbers. */
export function BackupSetupPanel({ statusDetail }: { statusDetail: string }) {
  return (
    <PanelCard title="Backup collection is not configured" caption="No data is being received">
      <div className="flex flex-col gap-3">
        <Typography variant="body-sm" tone="muted">
          {statusDetail}
        </Typography>
        <Typography variant="body-sm" tone="muted">
          Veeam reaches NOVA by push: a scheduled collector on an on-premises host calls the Veeam
          API locally and posts a metadata-only summary to <code>/api/backup/ingest</code> with an
          Entra token. Azure Backup needs no collector at all — it is read on the Resource Graph
          poller with the access NOVA already holds.
        </Typography>
      </div>
    </PanelCard>
  );
}

/**
 * One `Freshness` per collector for the page header. The label names the source
 * and its host, so "which of the two is quiet" is answerable at a glance rather
 * than by reading a table.
 */
export function CollectorFreshness({ collector }: { collector: BackupCollectorState }) {
  const since = collector.received_at ?? collector.collected_at;
  if (!since) {
    return null;
  }
  return (
    <Freshness
      label={
        collector.host
          ? `${sourceLabel(collector.source)} · ${collector.host}`
          : sourceLabel(collector.source)
      }
      since={since}
      // The same 2× cadence the tiles use for staleness, so the dot and the
      // numbers agree about when trust runs out.
      staleAfter={collector.stale_after_sec * 1000}
    />
  );
}

/** The stale-collection warning, plus any partial-collection errors. */
export function CollectorBanner({ overview }: { overview: BackupOverview }) {
  const quiet = overview.collectors.filter((collector) => collector.stale);
  const parseProblems = overview.collectors.filter((collector) => collector.error_count > 0);

  return (
    <>
      {quiet.length > 0 && (
        <Alert
          tone="warning"
          title={
            overview.stale
              ? "Nothing is being collected — the numbers below are unknown, not green"
              : "One collection path has gone quiet"
          }
        >
          <ul className="flex flex-col gap-1">
            {quiet.map((collector) => (
              <li key={collector.source}>
                {sourceLabel(collector.source)}
                {collector.host ? ` (${collector.host})` : ""}{" "}
                {collector.status === "unknown"
                  ? "has never reported"
                  : `last reported ${formatRelativeTime(
                      collector.received_at ?? collector.collected_at,
                    )}`}{" "}
                — expected every {Math.round(collector.expected_cadence_sec / 60)} minutes.
                {/* A report that enumerates nothing is not liveness: it would
                    otherwise clear this banner and re-present the previous
                    report's rows as current. Name it, because "quiet" sends
                    someone to look at the network and this needs the service. */}
                {collector.empty_report_at && (
                  <>
                    {" "}
                    A report did arrive {formatRelativeTime(collector.empty_report_at)}, but it
                    carried no jobs at all — the collector is running and enumerating nothing.
                  </>
                )}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {parseProblems.length > 0 && (
        <Alert tone="warning" title="Part of the latest report could not be read">
          <ul className="flex flex-col gap-1">
            {parseProblems.map((collector) => (
              <li key={collector.source}>
                {sourceLabel(collector.source)}: {collector.error_count} of{" "}
                {collector.job_count + collector.error_count} rows did not parse, so those jobs are
                missing from every count on this board.
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Configuration the source could not read. Distinct from a parse error
          above: nothing was malformed and no row was lost — the jobs are all
          here, but VBR returned an error for some job pages, so their schedule
          is unknown and "did not run" cannot be judged for them. The collector
          reports `missed` as *undecided* for those, and the NOT NULL column
          stores it as false, which reads identically to "ran fine". Naming it
          is the difference between a known blind spot and a silent one. */}
      {(overview.jobs_config_partial > 0 || overview.jobs_missed_unknown > 0) && (
        <Alert tone="warning" title="Some job schedules could not be read">
          <ul className="flex flex-col gap-1">
            {overview.jobs_config_partial > 0 && (
              <li>
                {overview.jobs_config_partial} job
                {overview.jobs_config_partial === 1 ? "" : "s"} came back with partial configuration
                — the Veeam API returned an error for some job pages, which the collector stepped
                over. Their sessions are unaffected.
              </li>
            )}
            {overview.jobs_missed_unknown > 0 && (
              <li>
                {overview.jobs_missed_unknown} enabled job
                {overview.jobs_missed_unknown === 1 ? "" : "s"} could not be judged against the “did
                not run” tile: with no schedule, there is nothing to be late against. They are not
                counted as missed, and they are not proven fine either.
              </li>
            )}
          </ul>
        </Alert>
      )}

      {overview.errors.length > 0 && (
        <Alert
          tone="warning"
          title="Part of this could not be read — the tiles above are unknown, not green"
        >
          <ul className="flex flex-col gap-1">
            {overview.errors.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </Alert>
      )}
    </>
  );
}
